import { Box, Card as ChakraCard, Flex, FlexProps, Text } from '@chakra-ui/react';
import { PropsWithChildren, ReactNode, RefAttributes } from 'react';
import { Action, ActionMenu } from '../action-menu';
import { Tag } from './tag';

const colors = {
    standard: undefined,
    primary: 'purple',
    secondary: 'orange',
    mailing: 'red',
    error: 'red',
};

const barColors = {
    standard: '#FAF5FF',
    signature: '#FEFCBF',
    error: '#FFF5F5',
};

const borderColors = {
    standard: { base: 'musa.light.border', _dark: 'musa.dark.border' },
    error: '#FC8181',
    signature: '#E9D8FD',
};

interface CardProps extends PropsWithChildren {
    type?: keyof typeof colors;
    actions?: Action[];
    heading?: string;
    title?: string;
    barTitle?: string;
    barType?: keyof typeof barColors;
    barTagName?: string;
    slots?: {
        customTag?: ReactNode;
    };
}

export const Card = ({
    actions,
    children,
    type = 'standard',
    heading,
    title,
    barTitle,
    barType = 'standard',
    barTagName,
    slots,
    ...rest
}: CardProps & FlexProps & RefAttributes<HTMLDivElement>) => {
    const hasChildren = Array.isArray(children) ? children.some((child) => !!child) : !!children;
    return (
        <Flex gap={heading ? '12px' : 0} width="100%" flexDirection="column" {...rest}>
            {!!heading && <Text fontWeight={500}>{heading}</Text>}
            {barTitle && (
                <Flex
                    bgColor={barColors[barType]}
                    border="1px solid"
                    borderColor={borderColors[barType]}
                    minHeight="40px"
                    alignItems="center"
                    justifyContent="space-between"
                    p="10px 16px"
                    w="100%"
                    borderTopRadius="var(--card-radius, 0.375rem)">
                    <Text fontSize="sm" fontWeight={500} maxWidth="80%">
                        {barTitle}
                    </Text>

                    <Flex gap="8px" alignItems="center">
                        {slots?.customTag && slots.customTag}
                        <Flex gap="4px" alignItems="center">
                            {barTagName && (
                                <Tag
                                    textTransform="uppercase"
                                    fontWeight="400"
                                    variant="outline"
                                    borderRadius="2px"
                                    size="sm"
                                    h="16px"
                                    minH="16px"
                                    rounded="full">
                                    {barTagName}
                                </Tag>
                            )}
                        </Flex>
                        {(actions || type !== 'standard') && (
                            <Flex alignItems="center" h="20px" gap="8px">
                                {type !== 'standard' && (
                                    <Tag
                                        textTransform="uppercase"
                                        fontWeight="700"
                                        colorScheme={colors[type]}
                                        size="sm"
                                        h="16px"
                                        minH="16px"
                                        rounded="full">
                                        {type}
                                    </Tag>
                                )}
                                {actions && <ActionMenu actions={actions} />}
                            </Flex>
                        )}
                    </Flex>
                </Flex>
            )}
            <ChakraCard.Root
                w="100%"
                borderColor={borderColors[barType]}
                borderTopRadius={barTitle && 0}
                borderTop={barTitle && 0}
                variant="outline">
                <ChakraCard.Body p="16px" w="100%">
                    <Flex w="100%" flexDirection="column" gap="12px">
                        {((!barTitle && (type !== 'standard' || actions)) || title) && (
                            <Flex key="header" justifyContent="space-between" h="20px" alignItems="center">
                                {title ? (
                                    <Text fontSize="sm" fontWeight={500}>
                                        {title}
                                    </Text>
                                ) : (
                                    <Box />
                                )}
                                <Flex alignItems="center" h="20px" gap="8px">
                                    {type !== 'standard' && !barTitle && (
                                        <Tag
                                            textTransform="uppercase"
                                            fontWeight="700"
                                            colorScheme={colors[type]}
                                            size="sm"
                                            h="16px"
                                            minH="16px"
                                            rounded="full">
                                            {type}
                                        </Tag>
                                    )}
                                    {actions && !barTitle && <ActionMenu actions={actions} />}
                                </Flex>
                            </Flex>
                        )}
                        {hasChildren && (
                            <Flex key="body" w="100%" gap="12px" flexWrap="wrap" fontSize="14px">
                                {children}
                            </Flex>
                        )}
                    </Flex>
                </ChakraCard.Body>
            </ChakraCard.Root>
        </Flex>
    );
};
